import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  Checkbox,
  Typography,
  Box,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  IconButton,
  Avatar,
  Chip,
} from "@mui/material";
import {
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Upload as UploadIcon,
} from "@mui/icons-material";
import ContactApiService from "../services/ContactApiService";
import ContactImportDialog from "./ContactImportDialog";
import Contact from "../models/Contacts";
import { errorMessage } from "../shared/utils/validators";

async function loadAllContactsPaginated() {
  const all = [];
  let page = 1;
  let hasMore = true;
  while (hasMore) {
    const res = await ContactApiService.getContactsDetails({
      search: "",
      sortBy: "created_at",
      sortOrder: "desc",
      tag: "All",
      page,
      limit: 100,
    });
    if (res.success && res.data.contacts) {
      all.push(...res.data.contacts);
      hasMore = page < res.data.totalPages;
      page += 1;
    } else {
      hasMore = false;
    }
  }
  return all;
}

const CreateGroupDialog = ({ open, onClose, onSave, editGroup }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    contactsList: [],
  });
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [contacts, setContacts] = useState([]);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importDuplicatePool, setImportDuplicatePool] = useState([]);
  const [loadingImportPool, setLoadingImportPool] = useState(false);
  const [importFeedback, setImportFeedback] = useState(null);

  const normalizePhoneForDuplicateCheck = (phone = "") => {
    const digits = phone.replace(/\D/g, "");
    return digits.length > 10 ? digits.slice(-10) : digits;
  };

  useEffect(() => {
    ContactApiService.getContactsDetails({ limit: 10000}).then((res) => setContacts(res.data.contacts));
  }, []);

  useEffect(() => {
    if (open && editGroup) {
      setFormData({
        name: editGroup.attrs.name || "",
        description: editGroup.attrs.description || "",
        contactsList: editGroup.attrs.contactsList || [],
      });
      if (editGroup.attrs.contactsList) {
        const selectedIds = editGroup.attrs.contactsList.map(c => c.attrs && c.attrs.id);
        setContacts((prev) =>
          prev.map((contact) =>
            selectedIds.includes(contact.attrs.id)
              ? { ...contact, isSelected: true }
              : { ...contact, isSelected: false }
          )
        );
      }
      setActiveStep(0);
    } else if (open && !editGroup) {
      setFormData({
        name: "",
        description: "",
        contactsList: [],
      });
      setContacts((prev) => prev.map((c) => ({ ...c, isSelected: false })));
      setActiveStep(0);
    }
  }, [open, editGroup]);

  const steps = ["Group Details", "Contact Selection"];

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Group name is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (activeStep === 0 && validateStep1()) {
      setActiveStep(1);
    }
  };

  const handleBack = () => {
    setActiveStep(0);
  };

  const handleContactSelection = (contactId) => {
    setContacts((prev) =>
      prev.map((contact) =>
        contact.attrs.id === contactId
          ? { ...contact, isSelected: !contact.isSelected }
          : contact
      )
    );
  };

  const handleSelectAll = (event) => {
    const isChecked = event.target.checked;
    setContacts((prev) =>
      prev.map((contact) => ({ ...contact, isSelected: isChecked }))
    );
  };

  const handleSave = () => {
    const contactsList = contacts.filter((contact) => contact.isSelected);
    if (contactsList.length === 0) {
      setErrors({ contactsList: "Select at least one contact" });
      return;
    }
    const groupData = {
      ...formData,
      contactsList,
    };
    onSave(groupData);
    handleClose();
  };
  function getTagColor(color) {
    return color || "#757575";
  };

  const handleClose = () => {
    setActiveStep(0);
    setFormData({
      name: "",
      description: "",
      contactsList: [],
    });
    setErrors({});
    setSearchQuery("");
    setContacts((prev) => prev.map((contact) => ({ ...contact, isSelected: false })));
    setImportDialogOpen(false);
    setImportDuplicatePool([]);
    setLoadingImportPool(false);
    setImportFeedback(null);
    onClose();
  };

  const handleOpenImportForNewGroup = async () => {
    setImportDialogOpen(true);
    setLoadingImportPool(true);
    setImportFeedback(null);
    try {
      const all = await loadAllContactsPaginated();
      setImportDuplicatePool(all);
    } catch (e) {
      setImportDuplicatePool(contacts);
    } finally {
      setLoadingImportPool(false);
    }
  };

  const handleImportOneContact = async (contactData) => {
    try {
      const model = new Contact();
      model.setName(contactData.name);
      let phoneNumber = contactData.phone;
      const digits = phoneNumber.replace(/\D/g, "");
      if (digits.length === 11 || digits.length === 12) {
        phoneNumber = `+${digits}`;
      }
      model.setPhoneNumber(phoneNumber);
      const response = await ContactApiService.createContact(model);
      if (response?.success === false || response?.error) {
        return {
          success: false,
          error: response?.error || "Failed to create contact",
        };
      }
      const contactId =
        typeof response === "string" ? response : response?.id ?? null;
      if (contactId) {
        setImportDuplicatePool((prev) => [
          ...prev,
          new Contact({
            id: contactId,
            name: contactData.name,
            phoneNumber,
            tags: [],
          }),
        ]);
      }
      return { success: true, contactId };
    } catch (error) {
      const message = errorMessage(error);
      return { success: false, error: message };
    }
  };

  const handleImportDialogClose = async (success = 0, failed = 0, meta = {}) => {
    setImportDialogOpen(false);
    setImportDuplicatePool([]);
    setLoadingImportPool(false);
    const ids = new Set(meta?.importedContactIds || []);

    if (success > 0 || failed > 0) {
      const createdCount = Number(meta?.createdCount || 0);
      const updatedCount = Number(meta?.updatedCount || 0);
      const importSummary = [
        createdCount > 0 ? `${createdCount} created` : null,
        updatedCount > 0 ? `${updatedCount} updated` : null,
      ].filter(Boolean).join(", ");
      setImportFeedback({
        severity: failed > 0 ? "warning" : "success",
        text:
          failed > 0
            ? `Imported ${success} contact(s) (${importSummary || "processed"}), ${failed} failed. New contacts are selected below.`
            : `Imported ${success} contact(s)${importSummary ? ` (${importSummary})` : ""}. They are selected below.`,
      });
    }

    if (ids.size === 0) return;

    const prevSelected = new Set(
      contacts.filter((c) => c.isSelected).map((c) => c.attrs.id)
    );
    ids.forEach((id) => prevSelected.add(id));

    try {
      const all = await loadAllContactsPaginated();
      const next = all.map((c) =>
        Object.assign(c, { isSelected: prevSelected.has(c.attrs.id) })
      );
      setContacts(next);
      setErrors((e) => ({ ...e, contactsList: "" }));
    } catch (e) {
      setImportFeedback({
        severity: "warning",
        text: "Contacts were created. Refresh the dialog or list if they do not appear.",
      });
    }
  };

  const filteredContacts = contacts.filter(
    (contact) =>
      contact.attrs.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.attrs.phoneNumber.includes(searchQuery)
  );
  const selectedContactsCount = contacts.filter((contact) => contact.isSelected).length;
  const isAllSelected = contacts.length > 0 && selectedContactsCount === contacts.length;
  const isIndeterminate = selectedContactsCount > 0 && selectedContactsCount < contacts.length;

  const renderStep1 = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Card sx={{ border: "1px solid #e0e0e0", borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#333", mb: 2 }}>
            Group Details
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Group Name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              fullWidth
              size="small"
              placeholder="Enter group name"
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              fullWidth
              size="small"
              placeholder="Enter group description"
              multiline
              minRows={2}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );

  const renderStep2 = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Card sx={{ border: "1px solid #e0e0e0", borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#333", mb: 2 }}>
            Select Contacts
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {!editGroup && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<UploadIcon />}
                onClick={handleOpenImportForNewGroup}
                disabled={loadingImportPool}
                sx={{ alignSelf: "flex-start", textTransform: "none", borderRadius: 1.5 }}
              >
                Import from CSV / Excel
              </Button>
            )}
            {importFeedback && (
              <Alert
                severity={importFeedback.severity}
                onClose={() => setImportFeedback(null)}
                sx={{ fontSize: "0.875rem" }}
              >
                {importFeedback.text}
              </Alert>
            )}
            <TextField
              placeholder="Search contacts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ color: "#9e9e9e", mr: 0.5, fontSize: 18 }} />
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1.5,
                  backgroundColor: "#fafafa",
                  border: "1px solid #e0e0e0",
                  fontSize: "0.875rem",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                    borderColor: "#d0d0d0",
                  },
                  "&.Mui-focused": {
                    backgroundColor: "#fff",
                    borderColor: "#1976d2",
                  },
                  "& fieldset": {
                    border: "none",
                  },
                },
              }}
            />
            <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ backgroundColor: "#f5f5f5" }}>
                      <Checkbox
                        indeterminate={isIndeterminate}
                        checked={isAllSelected}
                        onChange={handleSelectAll}
                        sx={{ color: "#666" }}
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>
                      Name
                    </TableCell>
                    <TableCell sx={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>
                      Phone Number
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredContacts?.map((contact) => {
                    const displayTags = (contact?.attrs?.tags || []).filter(
                      (t) => t?.attrs?.name?.trim()
                    );
                    return (
                    <TableRow key={contact.attrs.id} sx={{ "&:hover": { backgroundColor: "#f8f9fa" } }}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={contact.isSelected}
                          onChange={() => handleContactSelection(contact.attrs.id)}
                          sx={{ color: "#666" }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: "#1976d2", fontSize: "0.875rem" }}>
                            <PersonIcon />
                          </Avatar>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              flexWrap: "wrap",
                              minWidth: 0,
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {contact?.attrs.name}
                            </Typography>
                            {displayTags.length > 0 && (
                              <Box
                                component="span"
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  flexWrap: "wrap",
                                }}
                              >
                                {displayTags.map((tag) => (
                                    <Chip
                                      key={tag.attrs.id}
                                      label={tag.attrs.name}
                                      size="small"
                                      sx={{
                                        backgroundColor: `${getTagColor(tag.attrs.color)}15`,
                                        color: getTagColor(tag.attrs.color),
                                        fontWeight: 500,
                                        fontSize: "0.75rem",
                                        fontFamily: "Poppins, sans-serif",
                                        height: 22,
                                      }}
                                    />
                                  ))}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{contact.attrs.phoneNumber}</Typography>
                      </TableCell>
                    </TableRow>
                  );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {selectedContactsCount > 0 && (
              <Alert severity="info" sx={{ fontSize: "0.875rem" }}>
                <Typography variant="body2">
                  {selectedContactsCount} contact(s) selected for group
                </Typography>
              </Alert>
            )}
            {errors.contactsList && (
              <Alert severity="error" sx={{ fontSize: "0.875rem" }}>
                <Typography variant="body2">{errors.contactsList}</Typography>
              </Alert>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, maxHeight: "90vh" } }}
    >
      <DialogTitle sx={{ pb: 1, borderBottom: "1px solid #e0e0e0" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: "#333" }}>
            {editGroup ? "Edit Group" : "Create Group"}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Stepper activeStep={activeStep} sx={{ mb: 2 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          {activeStep === 0 ? renderStep1() : renderStep2()}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, borderTop: "1px solid #e0e0e0", gap: 1 }}>
        {activeStep === 1 && (
          <Button
            onClick={handleBack}
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            sx={{ borderRadius: 1.5, textTransform: "none", px: 3 }}
          >
            Back
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        {activeStep === 0 ? (
          <Button
            onClick={handleNext}
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            sx={{
              borderRadius: 1.5,
              textTransform: "none",
              px: 3,
              background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)",
              },
            }}
          >
            Next
          </Button>
        ) : (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={selectedContactsCount === 0}
            sx={{
              borderRadius: 1.5,
              textTransform: "none",
              px: 3,
              background: "linear-gradient(135deg, #4caf50 0%, #45a049 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #45a049 0%, #3d8b40 100%)",
              },
              "&.Mui-disabled": {
                background: "#e0e0e0",
                color: "#999",
              },
            }}
          >
            {editGroup ? "Save Changes" : `Create Group (${selectedContactsCount})`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
    {!editGroup && (
      <ContactImportDialog
        open={importDialogOpen}
        onClose={handleImportDialogClose}
        onImport={handleImportOneContact}
        existingContacts={importDuplicatePool}
        loadingExistingContacts={loadingImportPool}
        groupHint="New contacts will be created and selected for this group. Finish with Create Group."
      />
    )}
    </>
  );
};

export default CreateGroupDialog;
